import map from 'lodash/map'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import toFinite from 'lodash/toFinite'
import findIndex from 'lodash/findIndex'
import size from 'lodash/size'
import range from 'lodash/range'
import filter from 'lodash/filter'
import some from 'lodash/some'

import { hexToRGBA } from '../../utils/colorConversions'
import { annuity } from '../../utils/financeCalculations'
import { getYearFromAge } from '../../utils/dateParser'
import { appColors } from '../../globalStyles'

const CHART_EVENT_POINT_RADIUS = 6

// Construct the `datasets` object of the projections
// in the shape required by chartjs
// see: https://www.chartjs.org/docs/latest/general/data-structures.html
// and see: https://www.chartjs.org/docs/latest/charts/line.html
export const buildProjectionDatasets = (state, props) => {
  const colors = get(props, 'colors')
  const showTicksInYears = get(props, 'showTicksInYears', false)

  const { showIncome } = state

  const requiredDataset = constructDatasetForProjection(state.requiredProjection, [], state, props)
  const actualDataset = constructDatasetForProjection(state.actualProjection, isEmpty(state.editedProjection) ? props.cashflowEvents : [] , state, props)
  const editedDataset = constructDatasetForProjection(state.editedProjection, props.cashflowEvents,state, props)

  return [
    // edited data

    !isEmpty(state.editedProjection) && {
      label: 'Scenario',
      data: editedDataset,

      fill: false,
      borderColor: colors.secondaryTwoColor,
      backgroundColor: colors.secondaryTwoColor,
      borderWidth: 3,
      tension: 0.1,
      pointRadius: pointRadiusCallback,
      pointHoverRadius: pointRadiusCallback,
      pointBackgroundColor: pointBackgroundColorCallback,
      pointBorderColor: appColors.white,
      pointBorderWidth: 2,
      pointHoverBorderWidth: 2,

      // Metadata for each dataset
      tickIndexes: [
        0,
        findIndex(state.editedDataset, { assets: 0 }),
        findIndex(state.editedDataset, { age: state.retirementAge }),
        size(state.editedDataset) - 1
      ],
      retirementAge: state.retirementAge,
      projectionType: showIncome ? 'income' : 'capital',
      tickType: showTicksInYears ? 'year' : 'age',
    },
    // actual data

    {
      label: 'Actual',
      data: actualDataset,

      fill: true,
      borderWidth: 0,
      tension: 0.1,
      backgroundColor: hexToRGBA(colors.secondaryOneColor, 0.3),
      borderColor: hexToRGBA(colors.secondaryOneColor, 0.3),
      pointRadius: pointRadiusCallback,
      pointHoverRadius: pointRadiusCallback,
      pointBackgroundColor: pointBackgroundColorCallback,
      pointBorderColor: appColors.white,
      pointBorderWidth: 2,
      pointHoverBorderWidth: 2,

      // Metadata for each dataset
      tickIndexes: [
        0,
        findIndex(state.actualProjection, { assets: 0 }),
        findIndex(state.actualProjection, { age: state.retirementAge }),
        size(state.actualProjection) - 1],
      retirementAge: state.retirementAge,
      projectionType: showIncome ? 'income' : 'capital',
      tickType: showTicksInYears ? 'year' : 'age',
    },
    // required data

    !isEmpty(state.requiredProjection) && {
      label: 'Required',
      data: requiredDataset,

      fill: false,
      borderColor: colors.primaryColor,
      backgroundColor: colors.primaryColor,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 0,

      // Metadata for each dataset
      tickIndexes: [],
      retirementAge: state.retirementAge,
      projectionType: showIncome ? 'income' : 'capital',
      tickType: showTicksInYears ? 'year' : 'age',
    },
  ].filter(Boolean)
}

const constructDatasetForProjection = (projection, cashflowEvents, state, props) => {
  const currentAge = get(props, 'chartData.age')
  const usePresentValue = get(props, 'usePresentValue')
  const inflation = get(props, 'inflation', 0.06)
  const showIncome = get(state, 'showIncome', false)
  const showTicksInYears = get(props, 'showTicksInYears', false)

  const projectionType = showIncome ? 'net_monthly_income' : 'assets'

  return map(projection, (dataPoint) => ({
    // Metadata for each point
    capital: {
      future: get(dataPoint, 'assets'),
      present: annuity(get(dataPoint, 'assets'), currentAge - toFinite(get(dataPoint, 'age')), inflation)
    },
    income: {
      future: get(dataPoint, 'net_monthly_income'),
      present: Math.round(annuity(get(dataPoint, 'net_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation))
    },
    age: get(dataPoint, 'age'),
    // True when a living annuity is drawing at the 17.5% legislated maximum at
    // this age. Used to mark the point on the chart.
    livingAnnuityAtMax: get(dataPoint, 'living_annuity_at_max_withdrawal', false),
    cashflowEvents: buildProjectionCashflowEventsForAge(cashflowEvents, currentAge, get(dataPoint, 'age'), inflation),

    // The x value for the line chart is required to be
    // a string.
    y: usePresentValue
      ? Math.round(annuity(get(dataPoint, projectionType), currentAge - toFinite(get(dataPoint, 'age')), inflation))
      : get(dataPoint, projectionType),
    // NOTE: Whether labels can change dynamically has not been tested.
    x: showTicksInYears
      ? `${getYearFromAge(get(dataPoint, 'age'), currentAge)}`
      : `${get(dataPoint, 'age')} yrs`
  }))
}

const buildProjectionCashflowEventsForAge = (cashflowEvents, currentAge, age, inflation) => {
  const cashflowEventsAtAge = filter(cashflowEvents, (cashflowEvent) => {
    const { start_age, end_age, frequency } = cashflowEvent
    return range(start_age, end_age + 1, frequency).includes(age)
  })

  // Add some extra data here to make it easier for chartjs
  // to work off of cashflowEvents
  return map(cashflowEventsAtAge, (cashflowEvent) => ({
    ...cashflowEvent,
    pointAge: age,
    currentAge,
    inflation: inflation
  }))
}

// See: https://www.chartjs.org/docs/latest/charts/line.html#point-styling
// See: https://www.chartjs.org/docs/latest/general/options.html#scriptable-options
export const pointRadiusCallback = (ctx) => {
  return (hasActiveCashflowEvent(ctx) || get(ctx, 'raw.livingAnnuityAtMax', false))
    ? CHART_EVENT_POINT_RADIUS
    : 0
}

// A living annuity drawing at its legislated maximum is marked in the warning
// colour to distinguish it from a cashflow event. Where both fall on the same
// age the cashflow event keeps its existing colour.
export const pointBackgroundColorCallback = (ctx) => {
  if (hasActiveCashflowEvent(ctx)) return appColors.system

  return get(ctx, 'raw.livingAnnuityAtMax', false) ? appColors.warning : appColors.system
}

const hasActiveCashflowEvent = (ctx) => some(get(ctx, 'raw.cashflowEvents', []), { status: 'active' })
